import { DateTime } from 'luxon';
import { Typography, Paper, CircularProgress, Alert, Box, Chip } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ApiCallStatsResponse } from '../services/monitor-api.service';

interface ApiCallsActivityChartProps {
    data: ApiCallStatsResponse | null;
    loading: boolean;
    error: string | null;
}

export function ApiCallsActivityChart({ data, loading, error }: ApiCallsActivityChartProps) {
    if (loading) {
        return (
            <Paper sx={{ p: 4, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper sx={{ p: 4, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Alert severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Paper>
        );
    }

    if (!data || !data.intervals || data.intervals.length === 0) {
        return (
            <Paper sx={{ p: 4, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">No API call data available</Typography>
            </Paper>
        );
    }

    // Transform data for recharts
    const chartData = data.intervals.map((interval) => ({
        time: DateTime.fromISO(interval.timestamp).toFormat('HH:mm'),
        fullTime: DateTime.fromISO(interval.timestamp).toFormat('MMM dd, HH:mm'),
        success: interval.success,
        failure: interval.failure,
    }));

    // Calculate total counts
    const totalSuccess = data.intervals.reduce((sum, interval) => sum + interval.success, 0);
    const totalFailures = data.intervals.reduce((sum, interval) => sum + interval.failure, 0);

    return (
        <Paper sx={{ p: 3, height: 400 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h2">API Calls (24 Hours)</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                        label={`Failure: ${totalFailures.toLocaleString()}`}
                        color="error"
                        variant={totalFailures == 0 ? 'outlined' : 'filled'}
                        size="small"
                    />
                    <Chip
                        label={`Success: ${totalSuccess.toLocaleString()}`}
                        color="success"
                        variant="outlined"
                        size="small"
                    />
                </Box>
            </Box>
            <ResponsiveContainer width="100%" height={320}>
                <LineChart
                    data={chartData}
                    margin={{
                        top: 5,
                        right: 10,
                        left: 0,
                        bottom: 5,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} width={35} />
                    <Tooltip
                        labelFormatter={(label, payload) => {
                            const item = payload?.[0]?.payload;
                            return item ? item.fullTime : label;
                        }}
                        formatter={(value, name) => [value, name]}
                        itemSorter={(item) => {
                            // Ensure Success appears before Failure
                            return item.dataKey === 'success' ? 0 : 1;
                        }}
                    />
                    <Legend />
                    <Line
                        type="monotone"
                        dataKey="success"
                        stroke="#4caf50"
                        name="Success"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 5, stroke: '#4caf50', strokeWidth: 2 }}
                    />
                    <Line
                        type="monotone"
                        dataKey="failure"
                        stroke="#f44336"
                        name="Failure"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 5, stroke: '#f44336', strokeWidth: 2 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </Paper>
    );
}
